import cn from 'classnames';
import PropTypes from 'prop-types';

import { evNavigationClick, evTopLevelNavigationClick } from 'events/headerFooter';

import { makeOpenLiveChat } from 'helpers/ClientUtils';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { track } from 'apis/amethyst';
import css from 'styles/components/hf/zappos/topBar.scss';
import marketplace from 'cfg/marketplace.json';
import DynamicBanner from 'components/hf/zappos/DynamicBanner';
import MartyErrorBoundary from 'components/common/MartyErrorBoundary';
import Link from 'components/hf/HFLink';

const { phoneNumber, phoneNumberTel, phoneNumberVip, phoneNumberVipTel, chatUrl, chatWindowName } = marketplace;

const openLiveChat = makeOpenLiveChat(chatWindowName);

const TopBar = ({ isOpen, handleLocaleChange, handleTopNavClick, handleTopNavCloseClick,
  isTopBannerShowing, hydraLocale, holmes,
  content, tbContent, hydraHFDynamicBanner, locale, DynamicBanner, Link }, { testId }) => {

  const isSpanishSelected = locale === 'es-US';
  const localeLabel = `Change language to ${isSpanishSelected ? 'English' : 'Spanish'}`;
  const makeHeaderLocale = () => (
    // OneLinkNoTx class preventing translation
    <div className={cn(css.dropdownList, css.headerLocale, 'OneLinkNoTx', { [css.hidden]: !hydraLocale })}>
      <Link
        to="/language"
        onClick={e => {
          handleTopNavClick(e);
          track(evTopLevelNavigationClick, {
            valueClicked: 'Header Choose language'
          });
        }}
        data-shyguy="headerLocaleHeaderDropdown"
        aria-expanded={isOpen('headerLocaleHeaderDropdown')}
        aria-label={localeLabel}
        title={localeLabel}
        id="header-locale-output">{isSpanishSelected ? 'ES' : 'EN'}</Link>
      <ul data-hfdropdown data-test-id={testId('headerLanguageDropdown')}>
        <li>Language</li>
        <li>
          <input
            data-test-id={testId('headerEngLocale')}
            type="radio"
            id="header-locale-en-US"
            name="locale"
            checked={!isSpanishSelected}
            onChange={handleLocaleChange}
            data-source="Header"
            value="en-US" />
          <label htmlFor="header-locale-en-US">English - EN</label>
        </li>
        <li>
          <input
            data-test-id={testId('headerEspLocale')}
            type="radio"
            id="header-locale-es-US"
            name="locale"
            checked={isSpanishSelected}
            onChange={handleLocaleChange}
            data-source="Header"
            value="es-US" />
          <label htmlFor="header-locale-es-US">Español - ES</label>
        </li>
        <li>
          <Link
            data-test-id={testId('headerLearnLocale')}
            onClick={() => {
              trackEvent('TE_HEADER_LOCALE_LEARN');
              track(evNavigationClick, {
                valueClicked: 'Learn More',
                parentDropdown: 'Header Choose language'
              });
            }}
            to="/c/language-settings">
            Learn More
          </Link>
        </li>
        <li className={css.close}>
          <button
            type="button"
            data-close
            onClick={handleTopNavCloseClick}
            aria-label={'Close language Menu'}/>
        </li>
      </ul>
    </div>
  );

  const isVip = holmes && holmes.vip;

  return (
    <div className={css.headerTopBar}>
      <div className="headerContainer">
        {makeHeaderLocale()}
        <div className={css.dropdownList}>
          <Link
            data-test-id={testId('headerCustomerServiceToggle')}
            to="/contact-us"
            onClick={e => {
              handleTopNavClick(e);
              track(evTopLevelNavigationClick, {
                valueClicked: 'Customer Service'
              });
            }}
            data-shyguy="customerServiceHeaderDropdown"
            aria-expanded={isOpen('customerServiceHeaderDropdown')}>
            Customer Service
          </Link>
          <ul data-hfdropdown data-test-id={testId('headerCustomerServiceDropdown')}>
            <li>
              <a
                onClick={e => {
                  openLiveChat(e);
                  trackEvent('TE_HEADER_CUSTOMER_SERVICE', 'Live Help');
                  trackLegacyEvent('Main-Nav', 'CustomerService', 'Live-Help');
                  track(evNavigationClick, {
                    valueClicked: 'Live Chat',
                    parentDropdown: 'Customer Service'
                  });
                }}
                data-test-id={testId('headerLiveChat')}
                href={chatUrl}>
                Live Chat
              </a>
            </li>
            <li>
              <Link
                to="/c/general-questions"
                data-test-id={testId('headerFAQS')}
                onClick={() => {
                  trackEvent('TE_HEADER_CUSTOMER_SERVICE', 'FAQs');
                  trackLegacyEvent('Main-Nav', 'CustomerService', 'FAQs');
                  track(evNavigationClick, {
                    valueClicked: 'FAQs',
                    parentDropdown: 'Customer Service'
                  });
                }}>
                FAQs
              </Link>
            </li>
            <li>
              <Link
                data-test-id={testId('headerContact')}
                to="/c/contact-us"
                onClick={() => {
                  trackEvent('TE_HEADER_CUSTOMER_SERVICE', 'Contact Info');
                  trackLegacyEvent('Main-Nav', 'CustomerService', 'Contact-Info');
                  track(evNavigationClick, {
                    valueClicked: 'Contact Info',
                    parentDropdown: 'Customer Service'
                  });
                }}>
                  Contact Info
              </Link>
            </li>
            <li>
              <a
                data-test-id={testId('headerFeedback')}
                href="https://www.zappos.com/survey/take/zappos-voc?source=header"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent('TE_HEADER_CUSTOMER_SERVICE', 'Give us Feedback');
                  trackLegacyEvent('Main-Nav', 'CustomerService', 'Give-us-Feedback');
                  track(evNavigationClick, {
                    valueClicked: 'Give us Feedback',
                    parentDropdown: 'Customer Service'
                  });
                }}>
                  Give us Feedback
              </a>
            </li>
            <li className={css.close}>
              <button
                type="button"
                data-close
                onClick={handleTopNavCloseClick}
                aria-label={'Close Customer Service Menu'}/>
            </li>
          </ul>
        </div>
        <a
          className={css.phoneNum}
          data-test-id={testId('headerPhone')}
          onClick={() => {
            trackEvent('TE_HEADER_CUSTOMER_SERVICE', 'Phone');
            trackLegacyEvent('Global', 'Header', '24-7-Help');
            track(evTopLevelNavigationClick, {
              valueClicked: 'Header Phone'
            });
          }}
          href={isVip ? phoneNumberVipTel : phoneNumberTel}>
          <span>Available 24/7 at </span>
          <strong>{isVip ? phoneNumberVip : phoneNumber}</strong>
        </a>
        {isTopBannerShowing &&
        <MartyErrorBoundary boundaryTag="TOPBAR">
          <DynamicBanner content={content} displayPhraseData={tbContent?.chosenPhrase} hydraHFDynamicBanner={hydraHFDynamicBanner} />
        </MartyErrorBoundary>}
      </div>
    </div>
  );
};

TopBar.defaultProps = {
  DynamicBanner,
  Link
};

TopBar.propTypes = {
  holmes: PropTypes.oneOfType([ PropTypes.bool, PropTypes.object ]).isRequired
};

TopBar.contextTypes = {
  testId: PropTypes.func
};

export default TopBar;
